import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  CAPTURE_TIMEOUT_SEC,
  captureFingerprint,
  checkDeviceStatus,
  classifyFingerprintScanError,
  fingerprintImageSrc,
} from "../../utils/mantraFingerprint";
import FingerprintInstallerDownloads from "./FingerprintInstallerDownloads";

const MIN_QUALITY_PERCENT = 75;
const PREPARE_WAIT_MS = 2000;
const PREPARE_PLACE_FINGER_MS = 2500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fingerprint capture control.
 * Reads scanner status on demand and returns the captured BitmapData.
 * Enrollment is left to the parent, so it can run on form submit.
 * @param {{
 *   value?: string;
 *   onCapture?: (result: { bitmapData: string; quality: number | null; nfiq: number | null }) => void;
 *   onClear?: () => void;
 *   disabled?: boolean;
 *   hint?: string;
 *   error?: string;
 * }} props
 */
const FingerprintCapture = ({
  value = "",
  onCapture,
  onClear,
  disabled = false,
  hint = "",
  error = "",
}) => {
  const [device, setDevice] = useState(null);
  const [checkingDevice, setCheckingDevice] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [capture, setCapture] = useState(null);
  const [scanError, setScanError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(null);
  /** @type {'idle' | 'checking' | 'wait' | 'place' | 'scanning'} */
  const [scanPhase, setScanPhase] = useState("idle");
  const scanAbortRef = useRef(false);
  // A status read that has not been consumed by a scan yet, so opening the
  // modal and scanning right away does not repeat the device calls.
  const deviceCheckFreshRef = useRef(false);

  const verified = Boolean(value?.trim());
  const deviceConnected = Boolean(device?.deviceConnected);
  const deviceChecked = device != null;
  const scanInProgress =
    scanPhase === "checking" ||
    scanPhase === "wait" ||
    scanPhase === "place" ||
    scanPhase === "scanning";
  const previewSrc = fingerprintImageSrc(capture?.bitmapData);
  const quality = capture?.quality;
  const qualityOk =
    quality != null && Number.isFinite(quality) && quality >= MIN_QUALITY_PERCENT;

  useEffect(
    () => () => {
      scanAbortRef.current = true;
    },
    []
  );

  useEffect(() => {
    if (scanPhase !== "scanning") {
      setSecondsLeft(null);
      return undefined;
    }
    setSecondsLeft(CAPTURE_TIMEOUT_SEC);
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev == null || prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [scanPhase]);

  const runDeviceCheck = async () => {
    setCheckingDevice(true);
    try {
      const status = await checkDeviceStatus();
      setDevice(status);
      deviceCheckFreshRef.current = status.deviceConnected;
      return status;
    } finally {
      setCheckingDevice(false);
    }
  };

  const resetScanFlow = () => {
    scanAbortRef.current = true;
    setScanPhase("idle");
    setCapture(null);
    setScanError("");
    setSecondsLeft(null);
  };

  const closeModal = () => {
    if (scanInProgress) return;
    resetScanFlow();
    setModalOpen(false);
  };

  const handleVerifyClick = async () => {
    if (disabled || verified || checkingDevice) return;

    const status = await runDeviceCheck();
    if (!status.deviceConnected) {
      toast.error(status.statusMessage);
      return;
    }

    scanAbortRef.current = false;
    setCapture(null);
    setScanError("");
    setScanPhase("idle");
    setModalOpen(true);
  };

  const runScan = async () => {
    scanAbortRef.current = false;
    setCapture(null);
    setScanError("");

    // connecteddevicelist → checkdevice must both pass before capture runs.
    let status = device;
    if (!deviceCheckFreshRef.current) {
      setScanPhase("checking");
      status = await runDeviceCheck();
      if (scanAbortRef.current) return;
      if (!status.deviceConnected) {
        setScanError(
          status.statusMessage ||
            "Scanner not connected. Check the device and tap Rescan."
        );
        setScanPhase("idle");
        return;
      }
    }
    deviceCheckFreshRef.current = false;

    setScanPhase("wait");
    await sleep(PREPARE_WAIT_MS);
    if (scanAbortRef.current) return;

    setScanPhase("place");
    await sleep(PREPARE_PLACE_FINGER_MS);
    if (scanAbortRef.current) return;

    setScanPhase("scanning");
    try {
      const result = await captureFingerprint({
        deviceModel: status?.deviceModel,
      });
      if (!scanAbortRef.current) {
        setCapture(result);
        if (
          result.quality != null &&
          Number.isFinite(result.quality) &&
          result.quality < MIN_QUALITY_PERCENT
        ) {
          setScanError(
            `Quality ${result.quality}% is too low. Need at least ${MIN_QUALITY_PERCENT}%. Rescan.`
          );
        }
      }
    } catch (err) {
      console.error(err);
      if (!scanAbortRef.current) {
        const classified = classifyFingerprintScanError(err);
        setScanError(classified.message);
        toast.error(classified.message);
      }
    } finally {
      if (!scanAbortRef.current) setScanPhase("idle");
    }
  };

  const acceptCapture = () => {
    if (!capture || !qualityOk) return;

    onCapture?.({
      bitmapData: capture.bitmapData,
      templateData: capture.templateData || capture.bitmapData,
      quality: capture.quality,
      nfiq: capture.nfiq,
    });

    resetScanFlow();
    setModalOpen(false);
    toast.success("Fingerprint captured.");
  };

  const handleClear = () => {
    if (disabled || scanInProgress) return;
    onClear?.();
  };

  const placeholderText = () => {
    if (previewSrc) return "";
    if (scanPhase === "checking") return "Checking scanner…";
    if (scanPhase === "wait") return "Please wait…";
    if (scanPhase === "place") {
      return "Place your finger firmly on the scanner and hold still.";
    }
    if (scanPhase === "scanning") return "Scanning your fingerprint…";
    if (scanError) return scanError;
    return "No fingerprint captured yet";
  };

  const phaseHint = () => {
    if (scanPhase === "checking") return "Confirming the scanner is connected…";
    if (scanPhase === "wait") return "Getting scanner ready. Please wait a moment.";
    if (scanPhase === "place") return "Ready to scan. Put your finger on the scanner now.";
    if (scanPhase === "scanning") return "Do not lift your finger.";
    return null;
  };

  const primaryActionLabel = () => {
    if (scanPhase === "checking") return "Checking device…";
    if (scanPhase === "wait") return "Please wait…";
    if (scanPhase === "place") return "Place your finger…";
    if (scanPhase === "scanning") {
      return secondsLeft != null
        ? `Scanning… ${secondsLeft}s`
        : "Scanning…";
    }
    if (scanError || (capture && !qualityOk)) return "Rescan";
    if (!capture) return "Start scan";
    return "Use this scan";
  };

  const handlePrimaryAction = () => {
    if (scanInProgress) return;
    if (capture && qualityOk) {
      acceptCapture();
      return;
    }
    void runScan();
  };

  return (
    <>
      <div className="mt-8 w-full border-t border-gray-200 pt-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={`btn px-6 !py-3 !rounded-full shrink-0 ${
              verified ? "!bg-emerald-600" : "!bg-danger"
            }`}
            onClick={handleVerifyClick}
            disabled={disabled || verified || checkingDevice}
          >
            {checkingDevice ? "Checking device…" : "Verify fingerprint"}
          </button>

          {deviceChecked && !deviceConnected && !verified ? (
            <button
              type="button"
              className="btn px-6 !py-3 !rounded-full shrink-0 !bg-primary"
              onClick={() => void runDeviceCheck()}
              disabled={disabled || checkingDevice}
            >
              Check device status
            </button>
          ) : null}

          {verified ? (
            <button
              type="button"
              className="text-sm text-primary underline hover:opacity-80 disabled:opacity-50"
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </button>
          ) : null}
        </div>

        {hint && !verified ? (
          <p className="text-sm text-gray-500 m-0 mt-3">{hint}</p>
        ) : null}

        {deviceChecked && !verified ? (
          <p
            className={`text-sm font-UbuntuMedium m-0 mt-3 ${
              deviceConnected ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            {device.statusMessage}
          </p>
        ) : null}

        <FingerprintInstallerDownloads
          visible={deviceChecked && !deviceConnected && !verified}
        />

        {verified ? (
          <p className="text-sm text-emerald-700 m-0 mt-3">Fingerprint captured</p>
        ) : null}

        {error ? <p className="text-danger small m-0 mt-2">{error}</p> : null}
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 bg-black/50 bg-opacity-70 flex justify-center items-center z-[9999] p-4">
          <div className="bg-white p-6 w-full max-w-md text-center font-UbuntuRegular rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Fingerprint scan</h2>
            <p className="text-sm text-gray-600 mb-4">
              Scan the driver&apos;s fingerprint using {device?.deviceModel || "the scanner"}.
            </p>

            <div className="mx-auto mb-3 flex h-[200px] w-[160px] items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Scanned fingerprint"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="px-3 text-center text-sm text-gray-500">
                  {placeholderText()}
                </span>
              )}
            </div>

            {scanPhase === "scanning" && secondsLeft != null ? (
              <p
                className={`text-2xl font-UbuntuMedium tabular-nums mb-2 ${
                  secondsLeft <= 5 ? "text-danger" : "text-primary"
                }`}
                aria-live="polite"
              >
                {secondsLeft}s
              </p>
            ) : null}

            <FingerprintInstallerDownloads
              visible={deviceChecked && !deviceConnected && scanPhase === "idle"}
            />

            {scanError && scanPhase === "idle" ? (
              <p className="text-sm font-UbuntuMedium text-amber-700 mb-3">{scanError}</p>
            ) : phaseHint() ? (
              <p className="text-sm font-UbuntuMedium text-primary mb-3" aria-live="polite">
                {phaseHint()}
              </p>
            ) : null}

            {capture && scanPhase === "idle" ? (
              <p
                className={`text-sm font-UbuntuMedium mb-4 ${
                  qualityOk ? "text-emerald-700" : "text-amber-700"
                }`}
              >
                Quality {quality}%
                {qualityOk
                  ? " — acceptable"
                  : ` — need at least ${MIN_QUALITY_PERCENT}%`}
              </p>
            ) : (
              <div className="mb-4" />
            )}

            <button
              type="button"
              onClick={handlePrimaryAction}
              disabled={scanInProgress}
              className="btn !bg-danger border-danger text-white w-full !py-3 mb-2 !rounded-full disabled:opacity-60"
            >
              {primaryActionLabel()}
            </button>

            <button
              type="button"
              onClick={closeModal}
              disabled={scanInProgress}
              className="btn border !bg-primary w-full !py-3 !rounded-full disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default FingerprintCapture;
